import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Star, MapPin, Phone, Globe, Clock, MessageSquare,
  ShieldCheck, Mail, ChevronLeft, ChevronRight,
  ExternalLink, Copy, Check, Calendar, TrendingUp,
  ArrowLeft, Users, Share2, Info, AlertCircle,
  Building2, Briefcase,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import ReviewCard from '../components/business/ReviewCard';
import ReviewFormDrawer from '../components/business/ReviewFormModal';
import SEO from '../components/common/SEO';
import Toast from '../components/common/Toast';
import axios from 'axios';

/* ─── Helpers ─────────────────────────────────────────────── */
const API = import.meta.env.VITE_API_BASE_URL || 'https://vmc-backend-vt2w.onrender.com/api';

const StarRow = ({ rating, size = 18, className = '' }) => (
  <div className={`flex items-center gap-0.5 ${className}`}>
    {[1, 2, 3, 4, 5].map(i => (
      <Star
        key={i}
        size={size}
        className={
          i <= Math.round(rating)
            ? 'fill-amber-400 text-amber-400'
            : 'fill-slate-200 text-slate-300'
        }
      />
    ))}
  </div>
);

/* ─── Skeleton Loader ─────────────────────────────────────── */
const SkeletonDetail = () => (
  <div className="min-h-screen bg-slate-50 animate-pulse">
    {/* Hero skeleton */}
    <div className="h-72 md:h-[380px] bg-gradient-to-br from-slate-300 via-slate-200 to-slate-300" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex-1 space-y-6">
          {/* Action bar */}
          <div className="flex gap-3">
            <div className="h-12 w-40 bg-slate-200 rounded-xl" />
            <div className="h-12 w-36 bg-slate-200 rounded-xl" />
          </div>
          {/* About card */}
          <div className="bg-white rounded-2xl p-7 space-y-3 border border-slate-100/80 shadow-sm">
            <div className="h-6 bg-slate-200 rounded-lg w-32" />
            <div className="h-4 bg-slate-100 rounded w-full" />
            <div className="h-4 bg-slate-100 rounded w-5/6" />
            <div className="h-4 bg-slate-100 rounded w-3/4" />
          </div>
          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-2xl h-28 border border-slate-100/80" />
            ))}
          </div>
          {/* Reviews */}
          <div className="space-y-4">
            <div className="h-7 bg-slate-200 rounded-lg w-40" />
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-2xl border border-slate-100/80 p-6 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-200" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 bg-slate-200 rounded w-28" />
                    <div className="h-3 bg-slate-100 rounded w-20" />
                  </div>
                </div>
                <div className="h-3 bg-slate-100 rounded w-full" />
                <div className="h-3 bg-slate-100 rounded w-4/5" />
              </div>
            ))}
          </div>
        </div>
        {/* Sidebar */}
        <div className="lg:w-80 xl:w-96 flex-shrink-0 space-y-5">
          <div className="bg-white rounded-2xl h-64 border border-slate-100/80" />
          <div className="bg-white rounded-2xl h-52 border border-slate-100/80" />
          <div className="bg-white rounded-2xl h-56 border border-slate-100/80" />
        </div>
      </div>
    </div>
  </div>
);

/* ─── Empty-State Card ────────────────────────────────────── */
const EmptyState = ({ icon: Icon = Info, title, message, action }) => (
  <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-8 text-center">
    <div className="w-14 h-14 rounded-2xl bg-slate-50 flex items-center justify-center mx-auto mb-4">
      <Icon size={24} className="text-slate-400" />
    </div>
    {title && <h4 className="font-semibold text-slate-700 mb-1.5">{title}</h4>}
    {message && <p className="text-sm text-slate-400 leading-relaxed max-w-sm mx-auto">{message}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

/* ─── Hero Gradient Palette ───────────────────────────────── */
const GRADIENTS = [
  'from-sky-900 via-sky-800 to-indigo-900',
  'from-slate-900 via-slate-800 to-sky-900',
  'from-indigo-900 via-purple-900 to-slate-900',
  'from-emerald-900 via-teal-900 to-slate-900',
  'from-rose-900 via-slate-900 to-indigo-900',
  'from-amber-900 via-orange-900 to-slate-950',
];

/* ═════════════════════════════════════════════════════════════
   BUSINESS DETAIL  PAGE
   ═════════════════════════════════════════════════════════════ */
const BusinessDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const heroRef = useRef(null);

  /* state */
  const [business, setBusiness] = useState(null);
  const [categoryName, setCategoryName] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedRatingFilter, setSelectedRatingFilter] = useState(null);
  const [stickyVisible, setStickyVisible] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [reviewPagination, setReviewPagination] = useState({
    offset: 0, limit: 10, total: 0, hasMore: false,
  });

  /* ── Sticky scroll listener ────────────────────────────── */
  useEffect(() => {
    const onScroll = () => {
      const bottom = heroRef.current?.getBoundingClientRect().bottom ?? 0;
      setStickyVisible(bottom < 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* ── Fetch business ────────────────────────────────────── */
  useEffect(() => {
    const fetchBusiness = async () => {
      try {
        const res = await axios.get(`${API}/businesses/${id}`);
        if (res.data.status === 'success') {
          setBusiness(res.data.data);
          // Fetch category name
          if (res.data.data.category_id) {
            try {
              const catRes = await axios.get(`${API}/categories/${res.data.data.category_id}`);
              if (catRes.data?.data?.name) setCategoryName(catRes.data.data.name);
            } catch {
              // Category lookup failed – silently ignore
            }
          }
        }
      } catch {
        setToast({ type: 'error', message: 'Failed to load business details.' });
      } finally {
        setLoading(false);
      }
    };
    fetchBusiness();
  }, [id]);

  /* ── Fetch reviews ─────────────────────────────────────── */
  useEffect(() => {
    if (!business) return;
    const fetchReviews = async () => {
      setReviewsLoading(true);
      try {
        const params = new URLSearchParams({
          businessId: id,
          limit: reviewPagination.limit,
          offset: reviewPagination.offset,
        });
        if (selectedRatingFilter) params.append('rating', selectedRatingFilter);
        const res = await axios.get(`${API}/reviews?${params}`);
        if (res.data.status === 'success') {
          setReviews(res.data.data);
          setReviewPagination(prev => ({ ...prev, ...res.data.pagination }));
        }
      } catch {
        setToast({ type: 'error', message: 'Failed to load reviews.' });
      } finally {
        setReviewsLoading(false);
      }
    };
    fetchReviews();
  }, [id, business, selectedRatingFilter, reviewPagination.offset]);

  /* ── Derived ───────────────────────────────────────────── */
  const canWriteReview = isAuthenticated && user?.role === 'consumer';

  /* ── Actions ───────────────────────────────────────────── */
  const handleWriteReview = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { returnTo: `/businesses/${id}` } });
      return;
    }
    if (user?.role !== 'consumer') {
      setToast({ type: 'error', message: 'Only consumers can submit reviews.' });
      return;
    }
    setShowReviewModal(true);
  };

  const handleReviewSubmitted = () => setReviewPagination(prev => ({ ...prev, offset: 0 }));

  const handleRatingFilterChange = (rating) => {
    setSelectedRatingFilter(prev => (prev === rating ? null : rating));
    setReviewPagination(prev => ({ ...prev, offset: 0 }));
  };

  const handleNextPage = () => {
    setReviewPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }));
    document.getElementById('reviews-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handlePrevPage = () => {
    setReviewPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }));
  };

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: business?.business_name, url });
      } catch { /* user cancelled */ }
    } else {
      handleCopy(url, 'share');
      setToast({ type: 'success', message: 'Link copied to clipboard!' });
    }
  };

  /* ── Loading ───────────────────────────────────────────── */
  if (loading) return <SkeletonDetail />;

  /* ── 404 ───────────────────────────────────────────────── */
  if (!business) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <SEO title="Business Not Found" />
        <div className="text-center bg-white rounded-3xl shadow-xl shadow-slate-200/60 p-14 max-w-md mx-4 border border-slate-100">
          <div className="w-20 h-20 bg-gradient-to-br from-slate-100 to-slate-50 rounded-3xl flex items-center justify-center mx-auto mb-7 ring-1 ring-slate-100">
            <AlertCircle size={36} className="text-slate-400" />
          </div>
          <h1 className="text-2xl font-extrabold text-slate-900 mb-3">Business Not Found</h1>
          <p className="text-slate-500 mb-8 leading-relaxed">
            The business you're looking for doesn't exist, may have been removed, or is not currently published.
          </p>
          <button
            onClick={() => navigate('/businesses')}
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-sky-600 text-white rounded-2xl font-semibold hover:bg-sky-700 active:scale-[0.97] transition-all shadow-lg shadow-sky-600/20"
          >
            <ArrowLeft size={18} /> Browse Businesses
          </button>
        </div>
      </div>
    );
  }

  /* ── Computed values ───────────────────────────────────── */
  const avgRating = business.rating?.average || 0;
  const totalReviews = business.rating?.total_reviews || 0;
  const distribution = business.rating?.distribution || {};

  const addressParts = [
    business.address?.street,
    business.address?.city,
    business.address?.state,
    business.address?.country,
  ].filter(Boolean);
  const fullAddress = addressParts.join(', ');

  const hasContact = business.contact?.phone || business.contact?.email || business.contact?.website || fullAddress;
  const hasHours = business.hours && Object.keys(business.hours).length > 0;

  const heroGradient = GRADIENTS[business.business_name.charCodeAt(0) % GRADIENTS.length];

  /* ═════════════════════════════════════════════════════════
     RENDER
     ═════════════════════════════════════════════════════════ */
  return (
    <div className="min-h-screen bg-slate-50">
      <SEO 
        title={`${business.business_name} - Verified Reviews`}
        description={`Read ${totalReviews} verified reviews for ${business.business_name}. Rated ${avgRating.toFixed(1)}/5 by the VMC community. ${business.description?.substring(0, 100)}...`}
      />

      {/* ─── Sticky Action Bar ──────────────────────────── */}
      <div
        className={`fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b border-slate-200/80 shadow-sm transition-all duration-300 ${
          stickyVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate('/businesses')}
              className="flex-shrink-0 w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 hover:text-slate-900 transition-all"
            >
              <ArrowLeft size={17} />
            </button>
            <div className="min-w-0">
              <p className="font-bold text-slate-900 truncate text-sm leading-tight">{business.business_name}</p>
              <div className="flex items-center gap-1.5">
                <StarRow rating={avgRating} size={11} />
                <span className="text-[11px] text-slate-400">
                  {avgRating.toFixed(1)} · {totalReviews} review{totalReviews !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleShare}
              className="flex-shrink-0 w-9 h-9 rounded-xl border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50 transition"
            >
              {copiedField === 'share' ? <Check size={15} className="text-emerald-500" /> : <Share2 size={15} />}
            </button>
            {canWriteReview && (
              <button
                onClick={() => setShowReviewModal(true)}
                className="flex-shrink-0 px-4 py-2 bg-sky-600 text-white text-sm font-semibold rounded-xl hover:bg-sky-700 active:scale-[0.97] transition-all"
              >
                Write a Review
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ─── Hero ───────────────────────────────────────── */}
      <div ref={heroRef} className="relative h-72 md:h-[380px] overflow-hidden">
        {/* Always show gradient background — logo is NOT a banner */}
        <div className={`w-full h-full bg-gradient-to-br ${heroGradient}`}>
          <span className="absolute inset-0 flex items-center justify-center text-white text-[200px] font-extrabold opacity-[0.06] select-none leading-none pointer-events-none">
            {business.business_name.charAt(0).toUpperCase()}
          </span>
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

        {/* Breadcrumb */}
        <div className="absolute top-5 left-0 right-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex items-center gap-1.5 text-sm text-white/60">
              <Link to="/" className="hover:text-white transition">Home</Link>
              <span className="text-white/30">/</span>
              <Link to="/businesses" className="hover:text-white transition">Businesses</Link>
              <span className="text-white/30">/</span>
              <span className="text-white/80 truncate max-w-[200px]">{business.business_name}</span>
            </nav>
          </div>
        </div>

        {/* Hero content overlay */}
        <div className="absolute bottom-0 left-0 right-0 pb-7">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="min-w-0 flex items-end gap-5">

                {/* ── Logo box (shown only if avatar_url exists) ── */}
                {business.avatar_url && (
                  <div className="flex-shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-2xl border-2 border-white/30 bg-white shadow-xl overflow-hidden">
                    <img
                      src={business.avatar_url}
                      alt={`${business.business_name} logo`}
                      className="w-full h-full object-contain p-2"
                      onError={e => { e.target.parentElement.style.display = 'none'; }}
                    />
                  </div>
                )}

                <div className="min-w-0">
                  {/* Badges row */}
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    {business.stats?.is_verified && (
                      <div className="inline-flex items-center gap-1.5 bg-emerald-500/90 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg">
                        <ShieldCheck size={12} /> Verified
                      </div>
                    )}
                    {categoryName && (
                      <div className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-full border border-white/20">
                        <Briefcase size={11} /> {categoryName}
                      </div>
                    )}
                  </div>

                  <h1 className="text-3xl md:text-4xl lg:text-[2.75rem] font-extrabold text-white drop-shadow-lg leading-tight">
                    {business.business_name}
                  </h1>

                  {fullAddress && (
                    <div className="flex items-center gap-1.5 mt-2.5 text-white/70 text-sm">
                      <MapPin size={13} className="flex-shrink-0" />
                      <span className="truncate">{fullAddress}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Rating pill */}
              {totalReviews > 0 && (
                <div className="flex-shrink-0 flex flex-col items-center bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-6 py-4 text-white shadow-2xl">
                  <span className="text-3xl font-extrabold leading-none">{avgRating.toFixed(1)}</span>
                  <StarRow rating={avgRating} size={13} className="my-1.5" />
                  <span className="text-xs text-white/60">{totalReviews} review{totalReviews !== 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ─── Page Body ──────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">

          {/* ══ Main Column ══════════════════════════════ */}
          <div className="flex-1 min-w-0 space-y-7">

            {/* Quick Actions */}
            <div className="flex flex-wrap items-center gap-3">
              {canWriteReview && (
                <button
                  onClick={handleWriteReview}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-sky-600 text-white font-semibold rounded-xl hover:bg-sky-700 active:scale-[0.97] transition-all shadow-sm"
                >
                  <Star size={17} className="fill-white" /> Write a Review
                </button>
              )}
              {!isAuthenticated && (
                <button
                  onClick={handleWriteReview}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-sky-600 text-white font-semibold rounded-xl hover:bg-sky-700 active:scale-[0.97] transition-all shadow-sm"
                >
                  <Star size={17} className="fill-white" /> Login to Review
                </button>
              )}
              {business.contact?.website && (
                <a
                  href={business.contact.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-3 border border-slate-200 bg-white text-slate-700 font-medium rounded-xl hover:bg-slate-50 hover:border-slate-300 transition text-sm"
                >
                  <ExternalLink size={15} /> Visit Website
                </a>
              )}
              <button
                onClick={handleShare}
                className="inline-flex items-center gap-2 px-5 py-3 border border-slate-200 bg-white text-slate-700 font-medium rounded-xl hover:bg-slate-50 hover:border-slate-300 transition text-sm"
              >
                {copiedField === 'share' ? <Check size={15} className="text-emerald-500" /> : <Share2 size={15} />}
                Share
              </button>
            </div>

            {/* ── About ─────────────────────────────────── */}
            <section className="bg-white rounded-2xl border border-slate-100/80 shadow-sm p-6 md:p-8">
              <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Building2 size={18} className="text-slate-400" />
                About
              </h2>
              {business.description ? (
                <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">{business.description}</p>
              ) : (
                <div className="flex items-start gap-3 py-2">
                  <Info size={18} className="text-slate-300 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-slate-400 italic">
                    This business hasn't added a description yet. Check back later for more information about their services.
                  </p>
                </div>
              )}
            </section>

            {/* ── Quick Stats ───────────────────────────── */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white rounded-2xl border border-slate-100/80 shadow-sm p-5 text-center group hover:border-sky-200 transition-colors">
                <p className="text-2xl font-extrabold text-slate-900">{avgRating > 0 ? avgRating.toFixed(1) : '—'}</p>
                <StarRow rating={avgRating} size={12} className="justify-center my-1.5" />
                <p className="text-xs text-slate-400 font-medium">Avg. Rating</p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-100/80 shadow-sm p-5 text-center group hover:border-sky-200 transition-colors">
                <p className="text-2xl font-extrabold text-slate-900">{totalReviews > 0 ? totalReviews : '—'}</p>
                <div className="flex justify-center my-1.5">
                  <Users size={14} className="text-slate-400" />
                </div>
                <p className="text-xs text-slate-400 font-medium">Total Reviews</p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-100/80 shadow-sm p-5 text-center group hover:border-sky-200 transition-colors">
                {business.stats?.response_rate > 0 ? (
                  <>
                    <p className="text-2xl font-extrabold text-emerald-600">{business.stats.response_rate}%</p>
                    <div className="flex justify-center my-1.5">
                      <MessageSquare size={14} className="text-emerald-400" />
                    </div>
                    <p className="text-xs text-slate-400 font-medium">Response Rate</p>
                  </>
                ) : (
                  <>
                    <p className="text-2xl font-extrabold text-slate-300">—</p>
                    <div className="flex justify-center my-1.5">
                      <MessageSquare size={14} className="text-slate-300" />
                    </div>
                    <p className="text-xs text-slate-400 font-medium">Response Rate</p>
                  </>
                )}
              </div>
            </div>

            {/* ── Reviews ───────────────────────────────── */}
            <section id="reviews-section">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                  <MessageSquare size={20} className="text-slate-400" />
                  Reviews
                  {totalReviews > 0 && (
                    <span className="ml-1 text-sm font-normal text-slate-400 bg-slate-100 px-2.5 py-0.5 rounded-full">
                      {totalReviews}
                    </span>
                  )}
                </h2>
                {selectedRatingFilter && (
                  <button
                    onClick={() => handleRatingFilterChange(selectedRatingFilter)}
                    className="text-xs font-semibold text-sky-600 hover:text-sky-800 transition flex items-center gap-1"
                  >
                    Clear filter ×
                  </button>
                )}
              </div>

              {reviewsLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="bg-white rounded-2xl border border-slate-100/80 p-6 animate-pulse">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full bg-slate-200" />
                        <div className="flex-1 space-y-2">
                          <div className="h-3 bg-slate-200 rounded w-28" />
                          <div className="h-3 bg-slate-100 rounded w-20" />
                        </div>
                        <div className="h-4 bg-slate-200 rounded w-20" />
                      </div>
                      <div className="space-y-2">
                        <div className="h-3 bg-slate-100 rounded w-full" />
                        <div className="h-3 bg-slate-100 rounded w-4/5" />
                        <div className="h-3 bg-slate-100 rounded w-2/3" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : reviews.length === 0 ? (
                <EmptyState
                  icon={MessageSquare}
                  title={selectedRatingFilter ? `No ${selectedRatingFilter}-Star Reviews` : 'No Reviews Yet'}
                  message={
                    selectedRatingFilter
                      ? `There are no ${selectedRatingFilter}-star reviews for this business. Try clearing the filter to see all reviews.`
                      : 'Be the first to share your experience! Your feedback helps others make informed decisions.'
                  }
                  action={
                    canWriteReview && !selectedRatingFilter ? (
                      <button
                        onClick={handleWriteReview}
                        className="px-5 py-2.5 bg-sky-600 text-white rounded-xl text-sm font-semibold hover:bg-sky-700 active:scale-[0.97] transition-all"
                      >
                        Write the First Review
                      </button>
                    ) : null
                  }
                />
              ) : (
                <div className="space-y-4">
                  {reviews.map(review => (
                    <ReviewCard key={review.id} review={review} />
                  ))}

                  {/* Pagination */}
                  {reviewPagination.total > reviewPagination.limit && (
                    <div className="flex items-center justify-center gap-3 pt-5">
                      <button
                        onClick={handlePrevPage}
                        disabled={reviewPagination.offset === 0}
                        className="flex items-center gap-1.5 px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft size={16} /> Previous
                      </button>
                      <span className="text-sm text-slate-500 font-medium tabular-nums">
                        Page {Math.floor(reviewPagination.offset / reviewPagination.limit) + 1} of{' '}
                        {Math.ceil(reviewPagination.total / reviewPagination.limit)}
                      </span>
                      <button
                        onClick={handleNextPage}
                        disabled={!reviewPagination.hasMore}
                        className="flex items-center gap-1.5 px-4 py-2.5 bg-sky-600 text-white rounded-xl text-sm font-semibold hover:bg-sky-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Next <ChevronRight size={16} />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </section>
          </div>

          {/* ══ Sidebar ══════════════════════════════════ */}
          <div className="lg:w-80 xl:w-96 flex-shrink-0 space-y-5">

            {/* ── Rating Breakdown ─────────────────────── */}
            <div className="bg-white rounded-2xl border border-slate-100/80 shadow-sm p-6">
              <h3 className="font-bold text-slate-900 mb-5 flex items-center gap-2">
                <Star size={16} className="text-amber-400 fill-amber-400" />
                Rating Breakdown
              </h3>

              {totalReviews > 0 ? (
                <>
                  <div className="flex items-center justify-center mb-5 pb-5 border-b border-slate-100">
                    <div className="text-center">
                      <p className="text-5xl font-extrabold text-slate-900 leading-none">{avgRating.toFixed(1)}</p>
                      <StarRow rating={avgRating} size={16} className="justify-center mt-2.5 mb-1.5" />
                      <p className="text-xs text-slate-400">
                        {totalReviews} review{totalReviews !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {[5, 4, 3, 2, 1].map(star => {
                      const count = distribution[star] || 0;
                      const pct = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
                      const isActive = selectedRatingFilter === star;
                      return (
                        <button
                          key={star}
                          onClick={() => handleRatingFilterChange(star)}
                          className={`w-full flex items-center gap-2.5 rounded-xl px-3 py-2 transition-all ${
                            isActive ? 'bg-amber-50 ring-1 ring-amber-200' : 'hover:bg-slate-50'
                          }`}
                        >
                          <span className="text-xs font-semibold text-slate-500 w-3 text-right flex-shrink-0">{star}</span>
                          <Star size={11} className="fill-amber-400 text-amber-400 flex-shrink-0" />
                          <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-amber-400 to-amber-300 rounded-full transition-all duration-500"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="text-xs text-slate-400 w-6 text-right flex-shrink-0 tabular-nums">{count}</span>
                        </button>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="text-center py-4">
                  <div className="w-12 h-12 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <Star size={20} className="text-slate-300" />
                  </div>
                  <p className="text-sm text-slate-400">No rating data available yet.</p>
                  <p className="text-xs text-slate-300 mt-1">Reviews will appear here once submitted.</p>
                </div>
              )}
            </div>

            {/* ── Contact & Location ───────────────────── */}
            <div className="bg-white rounded-2xl border border-slate-100/80 shadow-sm p-6">
              <h3 className="font-bold text-slate-900 mb-5 flex items-center gap-2">
                <MapPin size={16} className="text-sky-500" />
                Contact & Location
              </h3>

              {hasContact ? (
                <div className="space-y-4">
                  {fullAddress && (
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl bg-sky-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <MapPin size={15} className="text-sky-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Address</p>
                        <p className="text-sm text-slate-700 leading-snug">{fullAddress}</p>
                      </div>
                    </div>
                  )}

                  {business.contact?.phone && (
                    <div className="flex items-start gap-3 group">
                      <div className="w-9 h-9 rounded-xl bg-sky-50 flex items-center justify-center flex-shrink-0">
                        <Phone size={15} className="text-sky-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Phone</p>
                        <div className="flex items-center gap-2">
                          <a href={`tel:${business.contact.phone}`} className="text-sm text-sky-600 hover:text-sky-800 font-medium transition">
                            {business.contact.phone}
                          </a>
                          <button
                            onClick={() => handleCopy(business.contact.phone, 'phone')}
                            className="opacity-0 group-hover:opacity-100 transition text-slate-400 hover:text-slate-600"
                          >
                            {copiedField === 'phone' ? <Check size={13} className="text-emerald-500" /> : <Copy size={13} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {business.contact?.email && (
                    <div className="flex items-start gap-3 group">
                      <div className="w-9 h-9 rounded-xl bg-sky-50 flex items-center justify-center flex-shrink-0">
                        <Mail size={15} className="text-sky-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Email</p>
                        <div className="flex items-center gap-2">
                          <a href={`mailto:${business.contact.email}`} className="text-sm text-sky-600 hover:text-sky-800 font-medium transition truncate">
                            {business.contact.email}
                          </a>
                          <button
                            onClick={() => handleCopy(business.contact.email, 'email')}
                            className="opacity-0 group-hover:opacity-100 transition text-slate-400 hover:text-slate-600 flex-shrink-0"
                          >
                            {copiedField === 'email' ? <Check size={13} className="text-emerald-500" /> : <Copy size={13} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {business.contact?.website && (
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl bg-sky-50 flex items-center justify-center flex-shrink-0">
                        <Globe size={15} className="text-sky-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Website</p>
                        <a
                          href={business.contact.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-sky-600 hover:text-sky-800 font-medium transition flex items-center gap-1.5 min-w-0"
                        >
                          <span className="truncate">{business.contact.website.replace(/^https?:\/\//, '')}</span>
                          <ExternalLink size={12} className="flex-shrink-0" />
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <div className="w-12 h-12 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <MapPin size={20} className="text-slate-300" />
                  </div>
                  <p className="text-sm text-slate-400">Contact information is not available for this business.</p>
                  <p className="text-xs text-slate-300 mt-1">The business may update this in the future.</p>
                </div>
              )}
            </div>

            {/* ── Hours of Operation ───────────────────── */}
            <div className="bg-white rounded-2xl border border-slate-100/80 shadow-sm p-6">
              <h3 className="font-bold text-slate-900 mb-5 flex items-center gap-2">
                <Clock size={16} className="text-slate-400" />
                Hours of Operation
              </h3>

              {hasHours ? (
                <div className="space-y-1.5">
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => {
                    const h = business.hours[day];
                    const todayName = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
                    const isToday = day === todayName;
                    return (
                      <div
                        key={day}
                        className={`flex items-center justify-between py-2.5 px-3 rounded-xl text-sm transition-colors ${
                          isToday ? 'bg-sky-50 ring-1 ring-sky-100' : 'hover:bg-slate-50'
                        }`}
                      >
                        <span className={`capitalize font-medium ${isToday ? 'text-sky-700' : 'text-slate-600'}`}>
                          {day.slice(0, 3)}
                          {isToday && (
                            <span className="ml-2 text-[10px] bg-sky-600 text-white px-1.5 py-0.5 rounded-full font-semibold">
                              Today
                            </span>
                          )}
                        </span>
                        <span className={isToday ? 'text-sky-700 font-semibold' : 'text-slate-400'}>
                          {!h ? '—' : h.closed ? 'Closed' : `${h.open} – ${h.close}`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-4">
                  <div className="w-12 h-12 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <Clock size={20} className="text-slate-300" />
                  </div>
                  <p className="text-sm text-slate-400">Business hours have not been listed yet.</p>
                  <p className="text-xs text-slate-300 mt-1">Contact the business directly for availability.</p>
                </div>
              )}
            </div>

            {/* ── Business Info ─────────────────────────── */}
            <div className="bg-white rounded-2xl border border-slate-100/80 shadow-sm p-6">
              <h3 className="font-bold text-slate-900 mb-5 flex items-center gap-2">
                <Building2 size={16} className="text-slate-400" />
                Business Info
              </h3>
              <div className="space-y-4">
                {/* Verified status */}
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    business.stats?.is_verified ? 'bg-emerald-50' : 'bg-slate-100'
                  }`}>
                    <ShieldCheck size={15} className={business.stats?.is_verified ? 'text-emerald-600' : 'text-slate-400'} />
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Status</p>
                    <p className={`text-sm font-semibold ${business.stats?.is_verified ? 'text-emerald-600' : 'text-slate-500'}`}>
                      {business.stats?.is_verified ? 'Verified Business' : 'Unverified'}
                    </p>
                  </div>
                </div>

                {/* Category */}
                {categoryName && (
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0">
                      <Briefcase size={15} className="text-purple-500" />
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Category</p>
                      <p className="text-sm text-slate-700 font-medium">{categoryName}</p>
                    </div>
                  </div>
                )}

                {/* Member since */}
                {business.stats?.created_at && (
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Calendar size={15} className="text-slate-500" />
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Member Since</p>
                      <p className="text-sm text-slate-700">
                        {new Date(business.stats.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                        })}
                      </p>
                    </div>
                  </div>
                )}

                {/* Total reviews */}
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                    <TrendingUp size={15} className="text-amber-600" />
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Total Reviews</p>
                    <p className="text-sm font-semibold text-slate-700">
                      {totalReviews > 0 ? totalReviews : 'No reviews yet'}
                    </p>
                  </div>
                </div>

                {/* Owner info */}
                {business.owner?.name && (
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-sky-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {business.owner.avatar ? (
                        <img src={business.owner.avatar} alt={business.owner.name} className="w-full h-full object-cover" />
                      ) : (
                        <Users size={15} className="text-sky-500" />
                      )}
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Owner</p>
                      <p className="text-sm text-slate-700 font-medium">{business.owner.name}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ─── Review Modal ───────────────────────────────── */}
      <ReviewFormDrawer
        businessId={id}
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        onReviewSubmitted={handleReviewSubmitted}
      />

      {/* ─── Toast ──────────────────────────────────────── */}
      {toast && (
        <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />
      )}
    </div>
  );
};

export default BusinessDetailPage;